import { z } from "zod";
import { TopolBlock } from "./structures/columnSchema";
import { emailTranslatableBlockTags } from "@topol/utils";

export const TranslationBlockSchema = z.object({
  content: z.string().optional().catch(""),
  attributes: z
    .object({
      href: z.string().trim().optional().catch(""),
      src: z.string().trim().optional().catch(""),
      alt: z.string().optional().catch(""),
      title: z.string().optional().catch(""),
      videoSrc: z.string().trim().optional().catch(""),
    })
    .optional()
    .catch({}),
  children: z
    .array(
      z.object({
        attributes: z
          .object({
            href: z.string().trim().optional().catch(""),
            alt: z.string().optional().catch(""),
            // mj-rating per-segment translatable fields
            label: z.string().optional().catch(""),
            src: z.string().trim().optional().catch(""),
            value: z.union([z.string(), z.number()]).optional().catch(""),
          })
          .optional()
          .catch({}),
      })
    )
    .optional()
    .catch(undefined),
  items: z.array(z.any()).optional().catch(undefined),
  // mj-rating top-level translatable text fields (kept top-level to mirror
  // the block shape, since rating stores text props at top-level too).
  questionText: z.string().optional().catch(""),
  captionTextLeft: z.string().optional().catch(""),
  captionTextRight: z.string().optional().catch(""),
  urlPattern: z.string().optional().catch(""),
});

export const TranslationSchema = z.record(z.string(), TranslationBlockSchema);

export const LanguageItemSchema = z.object({
  key: z.string().min(1),
  primary: z.boolean().default(false),
  translations: TranslationSchema,
});

export const TopolLanguageSchema = z.array(LanguageItemSchema);

export type TopolLanguages = z.infer<typeof TopolLanguageSchema>;
export type TopolTranslation = z.infer<typeof TranslationSchema>;
export type TopolTranslationBlock = z.infer<typeof TranslationBlockSchema>;
export type TranslationAttributes = keyof Exclude<
  TopolTranslationBlock["attributes"],
  undefined
>;
export type TranslationChildAttributes = keyof Exclude<
  Exclude<TopolTranslationBlock["children"], undefined>[number]["attributes"],
  undefined
>;

export type ITranslatableBlockTag = (typeof emailTranslatableBlockTags)[number];

export type TopolTranslatableBlock = Extract<
  TopolBlock,
  { tagName: (typeof emailTranslatableBlockTags)[number] }
>;

export const translatableBlockAttributes = {
  "mj-button": ["href"],
  "mj-image": ["src", "alt", "title", "href", "videoSrc"],
  "mj-gif": ["src", "alt", "href"],
};

// Top-level translatable props (not in `attributes`).
// - mj-rating: text fields stored at top-level of the block.
// - mj-feed-block: `items` array; per-item merging is done by `item.id`
//   via mergeItemWithTranslation, not by index.
export const translatableBlockProps = {
  "mj-rating": [
    "questionText",
    "captionTextLeft",
    "captionTextRight",
    "urlPattern",
  ],
  "mj-feed-block": ["items"],
};

export function isTranslatableAttribute(
  attr: string,
  blockTag: string
): boolean {
  const attrs =
    translatableBlockAttributes[
      blockTag as keyof typeof translatableBlockAttributes
    ];
  if (!attrs) {
    return false;
  }
  return attrs.includes(attr);
}

export function isTranslatableProp(prop: string, blockTag: string): boolean {
  const props =
    translatableBlockProps[blockTag as keyof typeof translatableBlockProps];
  if (!props) {
    return false;
  }
  return props.includes(prop);
}

export const translatableBlockChildAttributes = {
  "mj-social": ["href", "alt"],
  "mj-rating": ["label", "alt", "href", "src", "value"],
};

export function isTranslatableChildAttribute(
  attr: string,
  blockTag: string
): boolean {
  const attrs =
    translatableBlockChildAttributes[
      blockTag as keyof typeof translatableBlockChildAttributes
    ];
  if (!attrs) {
    return false;
  }
  return attrs.includes(attr);
}

export const nonTranslatableLinkFields = [
  "href",
  "src",
  "videoSrc",
  "urlPattern",
  "value",
] as const;

export function isAiTranslatableField(field: string): boolean {
  return !nonTranslatableLinkFields.includes(
    field as (typeof nonTranslatableLinkFields)[number]
  );
}

// Feed-block (product) item fields that are NOT human-readable text and must
// never be translated: identifiers, links, the image source itself, prices and
// currency, dates. Everything else on a (static) item is treated as
// translatable text — this mirrors the block's part structure (image / href /
// price are their own types; the rest is text), so custom feed fields are
// covered too. `image-alt` / `image-title` are NOT here on purpose: they are
// the image's text and should be translated.
export const nonTranslatableFeedItemFields = [
  "id",
  "product_id",
  "product_feed_id",
  "value",
  "url",
  "href",
  "link",
  "image",
  "img_url",
  "image-width",
  "image-align",
  "image-fluid-on-mobile",
  "price",
  "price_before",
  "price_vat",
  "price_with_vat",
  "currency",
  "publishedat",
] as const;

export function isTranslatableFeedItemField(
  field: string,
  value: unknown
): boolean {
  if (typeof value !== "string" || value.trim() === "") return false;
  return !nonTranslatableFeedItemFields.includes(
    field as (typeof nonTranslatableFeedItemFields)[number]
  );
}

export type TranslationUnitTarget =
  | { kind: "content"; uid: string }
  | { kind: "baseAttr"; block: "mj-preview" | "mj-subject" }
  | { kind: "attr"; uid: string; attr: string }
  | { kind: "prop"; uid: string; prop: string }
  | { kind: "childAttr"; uid: string; childIndex: number; attr: string }
  | {
      kind: "feedItem";
      uid: string;
      itemIndex: number;
      itemId: string | number | undefined;
      field: string;
    };

export interface TranslationUnit {
  id: string;
  content: string;
  target: TranslationUnitTarget;
}

export const TopolSpecialBlockTranslationSchema = z.record(
  z.string(),
  TranslationSchema
);

export type TopolSpecialBlockTranslation = z.infer<
  typeof TopolSpecialBlockTranslationSchema
>;

export type SelectPrimaryBlockLanguage = {
  langs: string[];
  title: string;
  description: string;
};
